import React from "react"

interface VisibleDayContextType {
  visibleDayId: string | null
  setVisibleDayId: (dayId: string | null) => void
  reportIntersection: (dayId: string, ratio: number) => void
}

const defaultValue: VisibleDayContextType = {
  visibleDayId: null,
  setVisibleDayId: () => {},
  reportIntersection: () => {},
}

export const VisibleDayContext =
  React.createContext<VisibleDayContextType>(defaultValue)

export function VisibleDayProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [visibleDayId, setVisibleDayId] = React.useState<string | null>(null)
  const intersectionRatios = React.useRef<Map<string, number>>(new Map())

  const reportIntersection = React.useCallback(
    (dayId: string, ratio: number) => {
      intersectionRatios.current.set(dayId, ratio)

      // Find day with highest intersection ratio
      let maxRatio = 0
      let maxDayId: string | null = null
      intersectionRatios.current.forEach((r, id) => {
        if (r > maxRatio) {
          maxRatio = r
          maxDayId = id
        }
      })

      setVisibleDayId(maxDayId)
    },
    []
  )

  const value = React.useMemo(
    () => ({ visibleDayId, setVisibleDayId, reportIntersection }),
    [visibleDayId, reportIntersection]
  )

  return (
    <VisibleDayContext.Provider value={value}>
      {children}
    </VisibleDayContext.Provider>
  )
}

export function useVisibleDay() {
  return React.useContext(VisibleDayContext)
}
